import { useEffect, useState } from "react";
import { Copy, Loader2, Zap } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, photoUrl } from "@/lib/api";

const money = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);

function SuggestedRow({ draft, info }) {
  const photo = draft.photos?.[0];
  const label = !info ? "Checking eBay…"
    : info.source === "ebay" ? `eBay median of ${info.count} live listing${info.count === 1 ? "" : "s"}`
    : info.source === "ai" ? "AI estimate (no eBay comps found)" : "No price data";
  return (
    <li className="flex min-w-0 items-center gap-3 rounded-xl border border-slate-200 bg-white p-2.5" data-testid={`whatnot-suggest-row-${draft.id}`}>
      {photo ? (
        <img src={photoUrl(photo.id, "thumb", photo.rotated_at)} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" />
      ) : <div className="h-12 w-12 shrink-0 rounded-lg bg-slate-100" />}
      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 text-sm font-medium leading-snug text-slate-900">{draft.title || "Untitled draft"}</p>
        <p className="line-clamp-2 text-[11px] leading-snug text-slate-500" data-testid={`whatnot-suggest-source-${draft.id}`}>
          {label}{info?.low != null && info?.high != null ? ` · range ${money(info.low)}–${money(info.high)}` : ""}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <div className="min-w-[72px] rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-right font-mono text-sm font-semibold text-slate-900"
          data-testid={`whatnot-suggest-price-${draft.id}`} aria-label="eBay suggested price (reference only)">
          {info ? money(info.suggested) : <Loader2 className="ml-auto h-4 w-4 animate-spin text-slate-400" />}
        </div>
        {info?.suggested != null ? (
          <button type="button" aria-label="Copy suggested price" data-testid={`whatnot-suggest-copy-${draft.id}`}
            onClick={() => navigator.clipboard?.writeText(Number(info.suggested).toFixed(2)).then(() => toast.success("Price copied"), () => {})}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50">
            <Copy className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    </li>
  );
}

const localFallback = (d) => {
  const pr = d.price_research || {};
  return pr.avg ? { suggested: pr.avg, source: "ai", count: 0, low: pr.low, high: pr.high } : { suggested: null, source: null };
};

export default function WhatnotPushDialog({ open, onOpenChange, drafts, onConfirm, busy }) {
  const [info, setInfo] = useState({});
  const idKey = drafts.map((d) => d.id).slice(0, 50).join(",");

  useEffect(() => {
    if (!open || !idKey) return;
    const ids = idKey.split(",");
    setInfo({});
    api.post("/drafts/ebay-suggested", { ids })
      .then(({ data }) => {
        const got = Object.fromEntries((data.items || []).map((i) => [i.id, i]));
        setInfo(Object.fromEntries(drafts.slice(0, 50).map((d) => [d.id, got[d.id] || localFallback(d)])));
      })
      .catch(() => setInfo(Object.fromEntries(drafts.slice(0, 50).map((d) => [d.id, localFallback(d)]))));
  }, [open, idKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const n = drafts.length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] w-[calc(100vw-1.5rem)] max-w-lg grid-cols-[minmax(0,1fr)] overflow-y-auto rounded-2xl" data-testid="whatnot-push-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Post {n} item{n === 1 ? "" : "s"} to Whatnot</DialogTitle>
          <DialogDescription>
            eBay suggested price for each item — reference only, to help set your auction starting bid.
          </DialogDescription>
        </DialogHeader>
        <ul className="min-w-0 space-y-2" data-testid="whatnot-suggest-list">
          {drafts.slice(0, 50).map((d) => <SuggestedRow key={d.id} draft={d} info={info[d.id]} />)}
        </ul>
        {n > 50 ? <p className="text-xs text-slate-500">Showing prices for the first 50 items.</p> : null}
        <div className="mt-2 grid grid-cols-[auto_minmax(0,1fr)] gap-2">
          <Button variant="outline" className="h-11 rounded-full px-5" onClick={() => onOpenChange(false)} data-testid="whatnot-push-cancel">Cancel</Button>
          <Button className="h-11 rounded-full bg-[#1F1F1F] px-3 text-sm text-white hover:bg-black" disabled={busy} onClick={onConfirm} data-testid="whatnot-push-confirm">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}
            Push {n} to Whatnot
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
